import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ─────────────────────────────────────────────────────────────────────────
 * DocumentsInternesPanel — module socle « documents-base »
 * Espace documentaire PRIVÉ de l'agence (mandats signés, pièces, diagnostics).
 * Réutilisé sur la fiche bien ET la fiche contact.
 * NB : ces documents ne sortent JAMAIS de l'agence (aucune fuite publique).
 * ──────────────────────────────────────────────────────────────────────── */

const MAX_BYTES = 25 * 1024 * 1024;
const ACCEPT = '.pdf,.jpg,.jpeg,.png,.webp,.docx,.xlsx';
const ALLOWED_MIMES = new Set([
  'application/pdf', 'image/jpeg', 'image/jpg', 'image/png', 'image/webp',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
]);

// ─── Helpers format FR ───
export const formatSizeFR = (bytes) => {
  if (!bytes && bytes !== 0) return '';
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} Ko`;
  return `${(bytes / (1024 * 1024)).toFixed(bytes < 10 * 1024 * 1024 ? 1 : 0).replace('.', ',')} Mo`;
};
export const formatDateFR = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString('fr-FR'); } catch { return ''; }
};
const daysUntil = (iso) => {
  if (!iso) return null;
  const d = new Date(iso); const now = new Date();
  return Math.ceil((d - now) / (1000 * 60 * 60 * 24));
};

const mimeIcon = (mime, name = '') => {
  const m = (mime || '').toLowerCase(); const n = (name || '').toLowerCase();
  if (m.includes('pdf') || n.endsWith('.pdf')) return { i: 'bi-file-earmark-pdf', c: 'text-rose-500' };
  if (m.includes('image') || /\.(jpe?g|png|webp)$/.test(n)) return { i: 'bi-file-earmark-image', c: 'text-slate-500' };
  if (m.includes('word') || n.endsWith('.docx')) return { i: 'bi-file-earmark-word', c: 'text-blue-600' };
  if (m.includes('sheet') || n.endsWith('.xlsx')) return { i: 'bi-file-earmark-excel', c: 'text-emerald-600' };
  return { i: 'bi-file-earmark', c: 'text-slate-500' };
};

const getToken = () => localStorage.getItem('token') || '';
// Bus d'évènements léger : synchronise les différents affichages d'un même bien/contact
// (panneau Documents internes, uploads contextuels Mandat/Diagnostics) sur une même page.
export const emitDocsChanged = (entityId) => {
  try { window.dispatchEvent(new CustomEvent('documents:changed', { detail: { entityId } })); } catch { /* noop */ }
};
const getRole = () => {
  try { return (JSON.parse(localStorage.getItem('user') || '{}').role) || ''; } catch { return ''; }
};

// ─── Modale Upload ───
const UploadModal = ({ entityType, entityId, types, onClose, onUploaded }) => {
  const [file, setFile] = useState(null);
  const [typeDoc, setTypeDoc] = useState('autre');
  const [dateExp, setDateExp] = useState('');
  const [busy, setBusy] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  const selectedType = useMemo(() => types.find((t) => t.id === typeDoc), [types, typeDoc]);
  const showExp = selectedType?.expirable;

  const grouped = useMemo(() => {
    const out = {};
    types.forEach((t) => { (out[t.category] ||= []).push(t); });
    return out;
  }, [types]);

  const pick = (f) => {
    if (!f) return;
    if (!ALLOWED_MIMES.has((f.type || '').toLowerCase())) {
      toast.error('Format non autorisé. Acceptés : PDF, JPG, PNG, WebP, DOCX, XLSX.'); return;
    }
    if (f.size > MAX_BYTES) { toast.error('Fichier trop volumineux (maximum 25 Mo).'); return; }
    setFile(f);
  };

  const submit = async () => {
    if (!file) { toast.error('Sélectionnez un fichier.'); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('entity_type', entityType);
      fd.append('entity_id', entityId);
      fd.append('type_document', typeDoc);
      if (showExp && dateExp) fd.append('date_expiration', dateExp);
      const r = await axios.post(`${API}/documents/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Document ajouté.');
      onUploaded?.(r.data);
      emitDocsChanged(entityId);
      onClose();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : (d?.message || 'Échec de l\'envoi du document.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-[60] p-4" onClick={onClose} data-testid="document-upload-modal">
      <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <h3 className="text-base font-bold text-slate-800">Ajouter un document</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="document-upload-close"><i className="bi bi-x-lg" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div
            onClick={() => inputRef.current?.click()}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => { e.preventDefault(); setDragOver(false); pick(e.dataTransfer.files?.[0]); }}
            className={`cursor-pointer border-2 border-dashed rounded-xl px-4 py-8 text-center transition ${dragOver ? 'border-blue-400 bg-blue-50' : 'border-slate-300 hover:border-blue-300 hover:bg-slate-50'}`}
            data-testid="document-upload-dropzone"
          >
            {file ? (
              <div className="flex items-center justify-center gap-2 text-sm font-medium text-slate-700">
                <i className={`bi ${mimeIcon(file.type, file.name).i} ${mimeIcon(file.type, file.name).c} text-xl`} />
                <span className="truncate max-w-[260px]">{file.name}</span>
                <span className="text-slate-400">· {formatSizeFR(file.size)}</span>
              </div>
            ) : (
              <>
                <i className="bi bi-cloud-arrow-up text-3xl text-slate-400" />
                <p className="text-sm text-slate-600 mt-1">Glissez un fichier ici ou <span className="text-blue-600 font-semibold">parcourez</span></p>
                <p className="text-[11px] text-slate-400 mt-0.5">PDF, JPG, PNG, WebP, DOCX, XLSX · 25 Mo max</p>
              </>
            )}
            <input ref={inputRef} type="file" accept={ACCEPT} className="hidden" onChange={(e) => pick(e.target.files?.[0])} data-testid="document-upload-input" />
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">Type de document</label>
            <select value={typeDoc} onChange={(e) => setTypeDoc(e.target.value)} className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500" data-testid="document-upload-type">
              {Object.entries(grouped).map(([cat, items]) => (
                <optgroup key={cat} label={cat}>
                  {items.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
                </optgroup>
              ))}
            </select>
          </div>

          {showExp && (
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">Date d'expiration <span className="text-slate-400 font-normal">(diagnostic)</span></label>
              <input type="date" value={dateExp} onChange={(e) => setDateExp(e.target.value)} className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500" data-testid="document-upload-expiration" />
            </div>
          )}
        </div>
        <div className="px-5 py-3 border-t border-slate-200 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 rounded-lg">Annuler</button>
          <button onClick={submit} disabled={busy || !file} className="px-4 py-2 text-sm font-semibold bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 text-white rounded-lg flex items-center gap-2" data-testid="document-upload-submit">
            {busy ? <i className="bi bi-arrow-repeat animate-spin" /> : <i className="bi bi-cloud-upload" />}
            {busy ? 'Envoi…' : 'Ajouter'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Modale Édition (renommage + type + date) ───
const EditModal = ({ doc, types, onClose, onSaved }) => {
  const [name, setName] = useState(doc.display_name || '');
  const [typeDoc, setTypeDoc] = useState(doc.type_document || 'autre');
  const [dateExp, setDateExp] = useState(doc.date_expiration || '');
  const [busy, setBusy] = useState(false);
  const showExp = useMemo(() => types.find((t) => t.id === typeDoc)?.expirable, [types, typeDoc]);
  const grouped = useMemo(() => {
    const out = {}; types.forEach((t) => { (out[t.category] ||= []).push(t); }); return out;
  }, [types]);

  const save = async () => {
    setBusy(true);
    try {
      const r = await axios.patch(`${API}/documents/${doc.id}`, {
        display_name: name, type_document: typeDoc,
        date_expiration: showExp ? (dateExp || null) : null,
      });
      toast.success('Document mis à jour.');
      onSaved?.(r.data); onClose();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Échec de la mise à jour.');
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-[60] p-4" onClick={onClose} data-testid="document-edit-modal">
      <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <h3 className="text-base font-bold text-slate-800">Modifier le document</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><i className="bi bi-x-lg" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">Nom</label>
            <input value={name} onChange={(e) => setName(e.target.value)} className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500" data-testid="document-edit-name" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">Type</label>
            <select value={typeDoc} onChange={(e) => setTypeDoc(e.target.value)} className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500" data-testid="document-edit-type">
              {Object.entries(grouped).map(([cat, items]) => (
                <optgroup key={cat} label={cat}>{items.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}</optgroup>
              ))}
            </select>
          </div>
          {showExp && (
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">Date d'expiration</label>
              <input type="date" value={dateExp ? dateExp.slice(0, 10) : ''} onChange={(e) => setDateExp(e.target.value)} className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500" data-testid="document-edit-expiration" />
            </div>
          )}
        </div>
        <div className="px-5 py-3 border-t border-slate-200 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 rounded-lg">Annuler</button>
          <button onClick={save} disabled={busy} className="px-4 py-2 text-sm font-semibold bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 text-white rounded-lg" data-testid="document-edit-save">{busy ? 'Enregistrement…' : 'Enregistrer'}</button>
        </div>
      </div>
    </div>
  );
};

// ─── Badge d'expiration ───
const ExpirationBadge = ({ iso, thresholdDays }) => {
  if (!iso) return null;
  const d = daysUntil(iso);
  if (d < 0) return <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-red-100 text-red-700">Expiré le {formatDateFR(iso)}</span>;
  if (d <= thresholdDays) return <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-100 text-amber-700">Expire le {formatDateFR(iso)}</span>;
  return <span className="text-[10px] text-slate-400">Valide jusqu'au {formatDateFR(iso)}</span>;
};

// ─── Ligne document ───
const DocRow = ({ doc, types, canDelete, onView, onDownload, onEdit, onDelete, thresholdDays }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const ic = mimeIcon(doc.mime_type, doc.filename_original);
  const typeLabel = types.find((t) => t.id === doc.type_document)?.label || doc.type_document;
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setMenuOpen(false); };
    document.addEventListener('mousedown', h); return () => document.removeEventListener('mousedown', h);
  }, []);

  return (
    <div className="flex items-center gap-3 p-3 bg-white rounded-lg border border-slate-200 hover:border-slate-300 transition" data-testid={`document-row-${doc.id}`}>
      <i className={`bi ${ic.i} ${ic.c} text-2xl shrink-0`} />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-slate-900 truncate" title={doc.display_name}>{doc.display_name}</p>
        <div className="flex items-center flex-wrap gap-x-2 gap-y-1 mt-1">
          <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-slate-100 text-slate-700">{typeLabel}</span>
          <span className="text-[11px] text-slate-400">{formatSizeFR(doc.size_bytes)}</span>
          <span className="text-[11px] text-slate-400">· {formatDateFR(doc.uploaded_at)}</span>
          {doc.uploaded_by_name && <span className="text-[11px] text-slate-400">· {doc.uploaded_by_name}</span>}
          <ExpirationBadge iso={doc.date_expiration} thresholdDays={thresholdDays} />
        </div>
      </div>
      <button onClick={() => onView(doc)} className="text-slate-500 hover:text-blue-600 p-1.5 rounded-md hover:bg-slate-50 transition" title="Voir" data-testid={`document-view-${doc.id}`}><i className="bi bi-eye" /></button>
      <button onClick={() => onDownload(doc)} className="text-slate-500 hover:text-blue-600 p-1.5 rounded-md hover:bg-slate-50 transition" title="Télécharger" data-testid={`document-download-${doc.id}`}><i className="bi bi-download" /></button>
      <div className="relative" ref={ref}>
        <button onClick={() => setMenuOpen((v) => !v)} className="text-slate-500 hover:text-slate-800 p-1.5 rounded-md hover:bg-slate-50 transition" title="Plus" data-testid={`document-menu-${doc.id}`}><i className="bi bi-three-dots-vertical" /></button>
        {menuOpen && (
          <div className="absolute right-0 mt-1 w-44 bg-white border border-slate-200 rounded-lg shadow-lg py-1 z-20">
            <button onClick={() => { setMenuOpen(false); onEdit(doc); }} className="w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2" data-testid={`document-edit-${doc.id}`}><i className="bi bi-pencil" /> Renommer / type</button>
            {canDelete && (
              <button onClick={() => { setMenuOpen(false); onDelete(doc); }} className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2" data-testid={`document-delete-${doc.id}`}><i className="bi bi-trash" /> Supprimer</button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const DocumentsInternesPanel = ({ entityType, entityId, canDelete: canDeleteProp, thresholdDays = 90, compact = false, collapsible = false, defaultOpen = true }) => {
  const [docs, setDocs] = useState([]);
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(collapsible ? defaultOpen : true);
  const canDelete = canDeleteProp !== undefined ? canDeleteProp : ['admin_agence', 'super_admin'].includes(getRole());

  // Taxonomie filtrée contextuellement selon l'entité de rattachement (bien vs contact)
  const formTypes = useMemo(() => {
    if (entityType === 'agence') return types;
    return types.filter((t) => !t.applies_to || (t.applies_to || []).includes(entityType));
  }, [types, entityType]);

  const load = useCallback(async () => {
    if (!entityId) { setLoading(false); return; }
    try {
      const r = await axios.get(`${API}/documents`, { params: { entity_type: entityType, entity_id: entityId } });
      setDocs(r.data || []);
    } catch {
      setDocs([]);
    } finally { setLoading(false); }
  }, [entityType, entityId]);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/document-types`).then((r) => { if (!cancelled) setTypes(r.data || []); }).catch(() => {});
    load();
    return () => { cancelled = true; };
  }, [load]);

  // Recharge quand un document du même bien/contact change ailleurs (upload contextuel, etc.)
  useEffect(() => {
    const h = (e) => { if (e.detail?.entityId === entityId) load(); };
    window.addEventListener('documents:changed', h);
    return () => window.removeEventListener('documents:changed', h);
  }, [entityId, load]);

  const onView = (doc) => {
    const tk = getToken();
    if (!tk) { toast.error('Session expirée, veuillez vous reconnecter.'); return; }
    window.open(`${API}/documents/${doc.id}/download?auth=${encodeURIComponent(tk)}`, '_blank', 'noopener');
  };
  const onDownload = async (doc) => {
    try {
      const r = await axios.get(`${API}/documents/${doc.id}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a'); a.href = url; a.download = doc.display_name || 'document';
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.status === 401 ? 'Session expirée, veuillez vous reconnecter.' : 'Téléchargement impossible.');
    }
  };
  const onDelete = async (doc) => {
    if (!window.confirm(`Supprimer définitivement « ${doc.display_name} » ?`)) return;
    try {
      await axios.delete(`${API}/documents/${doc.id}`);
      toast.success('Document supprimé.');
      setDocs((prev) => prev.filter((d) => d.id !== doc.id));
      emitDocsChanged(entityId);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Suppression impossible.');
    }
  };

  return (
    <div data-testid="documents-internes-panel">
      <div className="flex items-center justify-between mb-4">
        <button
          type="button"
          onClick={() => collapsible && setOpen((v) => !v)}
          className={`flex items-center gap-2 ${collapsible ? 'cursor-pointer' : 'cursor-default'}`}
          data-testid="documents-internes-toggle"
        >
          {collapsible && <i className={`bi bi-chevron-right text-slate-400 text-sm transition-transform ${open ? 'rotate-90' : ''}`} />}
          <h3 className="text-sm font-bold text-slate-800">Documents internes</h3>
          <span className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded-full text-[11px] font-bold">{docs.length}</span>
        </button>
        <button onClick={() => { setShowUpload(true); if (collapsible) setOpen(true); }} className="inline-flex items-center gap-1.5 h-9 px-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold shadow-sm transition" data-testid="document-add-btn">
          <i className="bi bi-plus-lg" /> <span className="hidden sm:inline">Ajouter un document</span>
        </button>
      </div>

      {open && (loading ? (
        <div className="text-center py-10 text-slate-400"><i className="bi bi-arrow-repeat animate-spin text-2xl" /></div>
      ) : docs.length === 0 ? (
        <div
          onClick={() => setShowUpload(true)}
          className="cursor-pointer text-center py-10 border-2 border-dashed border-slate-200 rounded-xl hover:border-blue-300 hover:bg-slate-50 transition"
          data-testid="documents-empty-state"
        >
          <i className="bi bi-folder2-open text-4xl text-slate-300" />
          <p className="text-sm font-medium text-slate-600 mt-2">Aucun document interne</p>
          <p className="text-xs text-blue-600 font-semibold mt-1">Ajouter le premier document</p>
          <p className="text-[11px] text-slate-400 mt-2 max-w-sm mx-auto">Mandats signés, diagnostics, pièces d'identité… Ces documents restent privés et ne sont jamais partagés publiquement.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {docs.map((doc) => (
            <DocRow key={doc.id} doc={doc} types={types} canDelete={canDelete}
              onView={onView} onDownload={onDownload} onEdit={setEditing} onDelete={onDelete} thresholdDays={thresholdDays} />
          ))}
        </div>
      ))}

      {showUpload && (
        <UploadModal entityType={entityType} entityId={entityId} types={formTypes}
          onClose={() => setShowUpload(false)}
          onUploaded={(d) => setDocs((prev) => [d, ...prev])} />
      )}
      {editing && (
        <EditModal doc={editing} types={formTypes}
          onClose={() => setEditing(null)}
          onSaved={(d) => setDocs((prev) => prev.map((x) => (x.id === d.id ? d : x)))} />
      )}
    </div>
  );
};

export default DocumentsInternesPanel;
